// src/user/ReferralTreeLazy.js
// Lazy-loading expandable/collapsible tree for Community Network
import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  Chip,
  Stack,
  IconButton,
  TextField,
  InputAdornment,
  Avatar,
  Breadcrumbs,
  Link,
  Tooltip,
  Divider,
} from "@mui/material";
import HomeIcon from "@mui/icons-material/Home";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import { SimpleTreeView } from "@mui/x-tree-view/SimpleTreeView";
import { TreeItem } from "@mui/x-tree-view/TreeItem";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { API, API_BASE_URL } from "../config/apiConfig";
import DashboardPageHeader from "../components/DashboardPageHeader";
import AccountTreeIcon from "@mui/icons-material/AccountTree";

/** Must match process.mlm_GetDirectReferralsWithDescendants tree depth (loaded UI). Earnings ActiveConnections uses 15 levels separately. */
const COMMUNITY_NETWORK_MAX_DEPTH = 10;

const ReferralTreeLazy = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [currentUserCode, setCurrentUserCode] = useState(null);
  const [rootNode, setRootNode] = useState(null);
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [loadedChildren, setLoadedChildren] = useState(new Map()); // Map<referralCode, children[]>
  const [loadingChildren, setLoadingChildren] = useState(new Set()); // Set<referralCode>
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [totalCommunityConnections, setTotalCommunityConnections] = useState(0); // Total connections from user profile
  const [adsWatchedData, setAdsWatchedData] = useState({}); // Map of UserID -> {adsWatched, targetAds, percentage, status}

  // Fetch current user info and initialize root node
  const initializeRoot = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem("token");
      if (!token) {
        setError("No token found. Please log in again.");
        setLoading(false);
        return;
      }

      const decoded = jwtDecode(token);
      const userId = decoded.UserID || decoded.userId;
      setCurrentUserId(userId);

      // Fetch user profile to get referral code
      const profileResponse = await axios.get(API.userProfile(userId), {
        headers: { Authorization: `Bearer ${token}` },
      });

      const user = profileResponse.data?.user || profileResponse.data;
      const referralCode = user.ReferralCode;
      setCurrentUserCode(referralCode);
      
      // Fetch earnings summary to get total community connections (same as Engagement Summary page)
      try {
        // Use the same endpoint pattern as EarningsSummary component
        const earningsResponse = await axios.get(`${API.earningsSummary}/${userId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        
        if (earningsResponse.data?.success && earningsResponse.data?.earnings) {
          const earnings = earningsResponse.data.earnings;
          // ActiveConnections is the field name returned by mlm_GetEarningsSummary stored procedure
          // This is the same value shown as "Community Connections" on Engagement Summary page
          const totalConnections = earnings.ActiveConnections || 0;
          setTotalCommunityConnections(totalConnections);
        }
      } catch (earningsErr) {
        console.error('❌ Error fetching earnings summary for community connections:', earningsErr);
        // Don't set error, just log it - we can still show the tree without total connections
      }

      // Create root node
      const root = {
        id: `user-${userId}`,
        userId: userId,
        referralCode: referralCode,
        name: `${user.FirstName || ""} ${user.LastName || ""}`.trim() || user.UserName || "You",
        userName: user.UserName || "",
        firstName: user.FirstName || "",
        lastName: user.LastName || "",
        email: user.Email || "",
        profilePicture: user.ProfilePicture || user.AvatarUrl || null,
        isActive: user.IsActive !== false,
        level: 0,
        hasChildren: true, // We assume root has children, will update after first load
        children: [],
      };

      setRootNode(root);
      setLoading(false);
    } catch (err) {
      console.error("❌ Error initializing root:", err);
      setError("Failed to load community network. Please try again.");
      setLoading(false);
    }
  }, []);

  // Fetch direct referrals for a given referral code
  const fetchDirectReferrals = useCallback(async (referralCode) => {
    if (loadingChildren.has(referralCode) || loadedChildren.has(referralCode)) {
      return; // Already loading or loaded
    }

    try {
      setLoadingChildren((prev) => new Set(prev).add(referralCode));

      const token = localStorage.getItem("token");
      const url = API.directReferrals(referralCode);
      console.log(`📡 [fetchDirectReferrals] Fetching from: ${url}`);
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      console.log(`✅ [fetchDirectReferrals] Response received:`, response.data);
      const referrals = response.data?.referrals || [];
      console.log(`📊 [fetchDirectReferrals] Found ${referrals.length} referrals`);
      
      // Store ads watched data if available
      if (response.data?.adsWatched) {
        setAdsWatchedData(prev => ({ ...prev, ...response.data.adsWatched }));
        console.log(`📊 [fetchDirectReferrals] Ads watched data received for ${Object.keys(response.data.adsWatched).length} users`);
      }
      
      // DEBUG: Log the RAW structure of referrals from API
      if (referrals.length > 0) {
        console.log(`🔍 [fetchDirectReferrals] FIRST REFERRAL RAW STRUCTURE:`, JSON.stringify({
          UserName: referrals[0].UserName,
          ReferralCode: referrals[0].ReferralCode,
          Level: referrals[0].Level,
          hasChildren: 'children' in referrals[0],
          childrenType: typeof referrals[0].children,
          childrenIsArray: Array.isArray(referrals[0].children),
          childrenLength: referrals[0].children?.length || 0,
          childrenRaw: referrals[0].children ? referrals[0].children.slice(0, 2) : null
        }, null, 2));
        
        // Check if flat data is available - THIS SHOWS ALL DESCENDANTS
        if (response.data?.flat && response.data.flat.length > 0) {
          const level1Count = response.data.flat.filter(r => r.Level === 1).length;
          const level2Count = response.data.flat.filter(r => r.Level === 2).length;
          const level3Count = response.data.flat.filter(r => r.Level === 3).length;
          const maxLevel = Math.max(...response.data.flat.map(r => r.Level));
          console.log(`📊 [fetchDirectReferrals] FLAT DATA (all descendants): total=${response.data.flat.length}, L1=${level1Count}, L2=${level2Count}, L3=${level3Count}, maxLevel=${maxLevel}`);
          
          // Show sample level 2 records to verify they exist
          const level2Samples = response.data.flat.filter(r => r.Level === 2).slice(0, 3);
          if (level2Samples.length > 0) {
            console.log(`📊 [fetchDirectReferrals] Sample Level 2 records:`, level2Samples.map(r => ({
              UserName: r.UserName,
              ReferralCode: r.ReferralCode,
              ReferredByCode: r.ReferredByCode,
              Level: r.Level
            })));
          }
        }
      }
      
      // Helper function to convert referral node to tree node format (handles nested children)
      const convertToTreeNode = (ref) => {
        const hasChildren = ref.children && Array.isArray(ref.children) && ref.children.length > 0;
        const refCode = ref.ReferralCode ? normalizeCode(ref.ReferralCode) : '';
        const totalReferrals = referralCounts.get(refCode) || 0;
        const node = {
          id: `user-${ref.UserID}`,
          userId: ref.UserID,
          referralCode: ref.ReferralCode,
          name: `${ref.FirstName || ""} ${ref.LastName || ""}`.trim() || ref.UserName || "Unknown",
          userName: ref.UserName || "",
          firstName: ref.FirstName || "",
          lastName: ref.LastName || "",
          email: ref.Email || "",
          profilePicture: null, // Add if available in response
          isActive: ref.IsActive !== false,
          level: ref.Level || 1,
          hasChildren: hasChildren,
          totalReferrals: totalReferrals, // Total count of all referrals (descendants)
          children: hasChildren ? ref.children.map(child => convertToTreeNode(child)) : [],
        };
        return node;
      };
      
      // WORKAROUND: If referrals don't have nested children but flat data does, rebuild tree from flat data
      let finalReferrals = referrals;
      const flatData = response.data?.flat || [];
      
      // Helper to normalize codes (define early so it can be used throughout)
      const normalizeCode = (code) => code ? code.trim().toUpperCase() : '';
      
      console.log(`🔍 [fetchDirectReferrals] Checking flat data: exists=${!!response.data?.flat}, length=${flatData.length}`);
      console.log(`🔍 [fetchDirectReferrals] Response structure:`, {
        hasReferrals: !!response.data?.referrals,
        referralsLength: referrals.length,
        hasFlat: !!response.data?.flat,
        flatLength: flatData.length,
        totalCount: response.data?.totalCount,
        level1Count: response.data?.level1Count,
        nestedCount: response.data?.nestedCount
      });
      
      // Calculate total referral counts for each user from flat data
      // Count ALL descendants (not just direct referrals) by traversing the tree
      const referralCounts = new Map();
      
      // Helper function to count all descendants recursively
      const countDescendants = (targetCode, data) => {
        let count = 0;
        const normalizedTarget = normalizeCode(targetCode);
        
        // Count direct referrals (level 1)
        const direct = data.filter(ref => 
          ref.ReferredByCode && normalizeCode(ref.ReferredByCode) === normalizedTarget
        );
        count += direct.length;
        
        // Recursively count descendants of direct referrals
        direct.forEach(child => {
          if (child.ReferralCode) {
            count += countDescendants(child.ReferralCode, data);
          }
        });
        
        return count;
      };
      
      // Initialize all referral codes and calculate their total referral counts
      const allReferralCodes = new Set();
      
      // Collect all referral codes from flat data
      flatData.forEach((ref) => {
        if (ref.ReferralCode) {
          allReferralCodes.add(ref.ReferralCode);
        }
      });
      
      // Also add level 1 referrals from the referrals array
      referrals.forEach((ref) => {
        if (ref.ReferralCode) {
          allReferralCodes.add(ref.ReferralCode);
        }
      });
      
      // Calculate total referral count for each referral code
      allReferralCodes.forEach((code) => {
        const count = countDescendants(code, flatData);
        referralCounts.set(normalizeCode(code), count);
      });
      
      console.log(`📊 [fetchDirectReferrals] Calculated referral counts for ${referralCounts.size} users`);
      // Log sample counts
      Array.from(referralCounts.entries()).slice(0, 5).forEach(([code, count]) => {
        console.log(`   - ${code}: ${count} total referrals`);
      });
      
      // If flat data is missing but we know descendants exist, we need to fetch them differently
      // For now, if referrals don't have children and flat is missing, try to fetch children for each level 1
      if (flatData.length === 0 && referrals.length > 0 && referrals[0].children?.length === 0) {
        console.log(`⚠️ [fetchDirectReferrals] No flat data and no nested children. Backend may not be sending descendants.`);
        console.log(`💡 [fetchDirectReferrals] This suggests the backend is using the fallback stored procedure (level 1 only).`);
        console.log(`💡 [fetchDirectReferrals] Check backend logs to see if mlm_GetDirectReferralsWithDescendants is being called.`);
      }
      
      if (flatData.length > 0 && referrals.length > 0) {
        const level1Count = referrals.length;
        const level2PlusCount = flatData.filter(r => r.Level > 1).length;
        const referralsChildrenLength = referrals[0]?.children?.length || 0;
        
        console.log(`🔍 [fetchDirectReferrals] Workaround check: level2PlusCount=${level2PlusCount}, referrals[0].children.length=${referralsChildrenLength}`);
        console.log(`🔍 [fetchDirectReferrals] Flat data sample (first 3):`, flatData.slice(0, 3).map(r => ({
          UserName: r.UserName,
          Level: r.Level,
          ReferralCode: r.ReferralCode,
          ReferredByCode: r.ReferredByCode
        })));
        
        // ALWAYS rebuild if flat data has more records than just level 1
        if (level2PlusCount > 0) {
          console.log(`🔧 [fetchDirectReferrals] WORKAROUND: Rebuilding tree from flat data (${flatData.length} total records, ${level2PlusCount} level 2+)`);
          console.log(`🔧 [fetchDirectReferrals] WORKAROUND: Rebuilding tree from flat data (${flatData.length} total records)`);
          
          // Build a map of all referrals by ReferralCode
          const refMap = new Map();
          flatData.forEach(ref => {
            refMap.set(ref.ReferralCode?.toUpperCase(), ref);
          });
          
          // Build tree structure from flat data
          const buildTreeFromFlat = (parentCode) => {
            const children = [];
            flatData.forEach(ref => {
              if (ref.ReferredByCode && normalizeCode(ref.ReferredByCode) === normalizeCode(parentCode)) {
                const child = {
                  ...ref,
                  children: buildTreeFromFlat(ref.ReferralCode)
                };
                children.push(child);
              }
            });
            return children;
          };
          
          // Rebuild referrals with nested children
          finalReferrals = referrals.map(l1Ref => ({
            ...l1Ref,
            children: buildTreeFromFlat(l1Ref.ReferralCode)
          }));
          
          console.log(`✅ [fetchDirectReferrals] Rebuilt tree: ${finalReferrals.length} level 1 with nested children`);
          finalReferrals.forEach(l1 => {
            console.log(`   - ${l1.UserName}: ${l1.children.length} direct children`);
          });
        }
      }
      
      // Convert to tree node format (recursively handle nested children)
      const children = finalReferrals.map((ref) => convertToTreeNode(ref));
      
      console.log(`🌳 [fetchDirectReferrals] Converted ${children.length} referrals with nested children`);
      // Log children count for debugging - VERIFY THE STRUCTURE
      let totalDescendants = 0;
      const logChildren = (nodes, depth = 0) => {
        nodes.forEach((child) => {
          const childCount = child.children?.length || 0;
          if (childCount > 0) {
            totalDescendants += childCount;
            console.log(`${'  '.repeat(depth)}   ✅ ${child.userName} (Level ${child.level}, Code: ${child.referralCode}): ${childCount} direct children`);
            // Log first child details
            if (child.children[0]) {
              console.log(`${'  '.repeat(depth + 1)}   → First child: ${child.children[0].userName} (Level ${child.children[0].level})`);
            }
            logChildren(child.children, depth + 1);
          } else {
            console.log(`${'  '.repeat(depth)}   ❌ ${child.userName} (Level ${child.level}): NO children`);
          }
        });
      };
      logChildren(children);
      console.log(`📊 [fetchDirectReferrals] Total descendants loaded: ${totalDescendants}`);
      
      // VERIFY: Check if children actually have the children property
      if (children.length > 0) {
        const firstL1 = children[0];
        console.log(`🔍 [fetchDirectReferrals] VERIFICATION - First Level 1 node:`, {
          userName: firstL1.userName,
          level: firstL1.level,
          referralCode: firstL1.referralCode,
          hasChildrenProp: 'children' in firstL1,
          childrenIsArray: Array.isArray(firstL1.children),
          childrenLength: firstL1.children?.length || 0,
          firstChild: firstL1.children?.[0] ? {
            userName: firstL1.children[0].userName,
            level: firstL1.children[0].level
          } : null
        });
      }

      // Store children in the map
      setLoadedChildren((prev) => {
        const newMap = new Map(prev);
        newMap.set(referralCode, children);
        
        // Also store nested children in the map by their parent's referral code
        // This allows the tree to display them when nodes are expanded
        let nestedStoredCount = 0;
        const storeNestedChildren = (nodes, parentCode) => {
          nodes.forEach((node) => {
            if (node.children && node.children.length > 0) {
              // Store this node's children
              newMap.set(node.referralCode, node.children);
              nestedStoredCount++;
              console.log(`💾 [fetchDirectReferrals] Stored children for ${node.userName} (${node.referralCode}): ${node.children.length} children`);
              // Recursively store children of children
              storeNestedChildren(node.children, node.referralCode);
            }
          });
        };
        storeNestedChildren(children, referralCode);
        console.log(`💾 [fetchDirectReferrals] Stored ${nestedStoredCount} nested referral codes in loadedChildren map`);
        
        return newMap;
      });

      // Update root node if it's the root
      if (referralCode === currentUserCode && rootNode) {
        // Root's total referrals is all records in flat data (all descendants)
        const rootRefCode = normalizeCode(referralCode);
        const rootCount = referralCounts.get(rootRefCode) || flatData.length;
        
        setRootNode((prev) => ({
          ...prev,
          children: children,
          hasChildren: children.length > 0,
          totalReferrals: rootCount, // Total count of all descendants
        }));
      }
    } catch (err) {
      console.error(`❌ Error fetching direct referrals for ${referralCode}:`, err);
      console.error(`❌ Error details:`, {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
        url: err.config?.url
      });
      // Set empty array on error so we don't keep retrying
      setLoadedChildren((prev) => {
        const newMap = new Map(prev);
        newMap.set(referralCode, []);
        return newMap;
      });
    } finally {
      setLoadingChildren((prev) => {
        const newSet = new Set(prev);
        newSet.delete(referralCode);
        return newSet;
      });
    }
  }, [currentUserCode, rootNode, loadingChildren, loadedChildren]);

  // Fetch ads watched data when root node is initialized
  useEffect(() => {
    if (rootNode?.referralCode && currentUserCode && !loadingChildren.has(rootNode.referralCode) && !loadedChildren.has(rootNode.referralCode)) {
      // Fetch ads watched data for the root user and their referral tree
      fetchDirectReferrals(rootNode.referralCode);
    }
  }, [rootNode?.referralCode, currentUserCode, fetchDirectReferrals, loadingChildren, loadedChildren]);

  // Build complete tree structure with all loaded children
  const buildCompleteTree = useCallback((node) => {
    if (!node) return null;

    // Get children from loadedChildren map (most up-to-date) OR use node's children if available
    // Priority: loadedChildren map > node.children > empty array
    let children = loadedChildren.get(node.referralCode);
    if (!children || children.length === 0) {
      children = node.children || [];
    }

    // Build complete tree recursively - children may already have nested children from API
    const builtChildren = children.map((child) => buildCompleteTree(child));
    
    if (children.length > 0 && node.level <= 1) {
      console.log(`🌳 [buildCompleteTree] Built tree for ${node.userName} (Level ${node.level}): ${builtChildren.length} children`);
    }
    
    return {
      ...node,
      children: builtChildren,
    };
  }, [loadedChildren]);

  // Find node by tree ID recursively
  const findNodeById = useCallback((node, targetId) => {
    if (!node) return null;
    if (node.id === targetId) return node;

    // Check children from loadedChildren map
    const children = loadedChildren.get(node.referralCode) || node.children || [];
    for (const child of children) {
      const found = findNodeById(child, targetId);
      if (found) return found;
    }
    return null;
  }, [loadedChildren]);

  // Handle node expansion
  const handleNodeToggle = useCallback((event, nodeIds) => {
    const newExpanded = new Set(nodeIds);
    const previousExpanded = expandedNodes;

    // Find newly expanded nodes (in newExpanded but not in previousExpanded)
    const newlyExpanded = Array.from(newExpanded).filter((id) => !previousExpanded.has(id));

    setExpandedNodes(newExpanded);

    // For each newly expanded node, fetch its children if not already loaded
    newlyExpanded.forEach((nodeId) => {
      const node = findNodeById(rootNode, nodeId);
      if (node && node.referralCode && !loadedChildren.has(node.referralCode)) {
        fetchDirectReferrals(node.referralCode);
      }
    });
  }, [rootNode, expandedNodes, loadedChildren, findNodeById, fetchDirectReferrals]);

  // Render tree item recursively
  const renderTreeItem = useCallback((node) => {
    if (!node) return null;

    const isRoot = node.level === 0;
    const isLoading = loadingChildren.has(node.referralCode);
    
    // Get children from loadedChildren map OR use node's children (which may already contain nested children from API)
    // Priority: loadedChildren map > node.children > empty array
    let children = loadedChildren.get(node.referralCode);
    const fromMap = !!children;
    if (!children || children.length === 0) {
      children = node.children || [];
    }
    
    // ALWAYS log for Level 1 nodes to debug
    if (node.level === 1) {
      console.log(`🔍 [renderTreeItem] Level 1 node ${node.userName} (Code: ${node.referralCode}):`, {
        fromMap,
        mapHasIt: loadedChildren.has(node.referralCode),
        mapChildrenLength: loadedChildren.get(node.referralCode)?.length || 0,
        nodeChildrenLength: node.children?.length || 0,
        finalChildrenLength: children.length,
        nodeHasChildrenProp: 'children' in node,
        nodeChildrenIsArray: Array.isArray(node.children),
        hasChildren: node.hasChildren
      });
    }
    
    // Debug logging for ALL nodes with children
    if (children.length > 0) {
      console.log(`🎨 [renderTreeItem] Rendering ${node.userName} (Level ${node.level}, ID: ${node.id}): ${children.length} children found (from ${fromMap ? 'map' : 'node.children'})`);
      console.log(`   Children:`, children.slice(0, 3).map(c => `${c.userName} (L${c.level})`).join(', '), children.length > 3 ? '...' : '');
    } else if (node.hasChildren) {
      console.log(`⚠️ [renderTreeItem] ${node.userName} (Level ${node.level}) has hasChildren=true but no children array!`);
    } else if (node.level === 1) {
      console.log(`❌ [renderTreeItem] ${node.userName} (Level 1) has NO children and hasChildren=false`);
    }
    
    const hasChildren = children.length > 0 || (!loadedChildren.has(node.referralCode) && node.hasChildren);

    return (
      <TreeItem
        key={node.id}
        itemId={node.id}
        label={
          <Box 
            sx={{ 
              display: "flex", 
              alignItems: "center", 
              gap: { xs: 0.75, sm: 1 }, 
              py: 0.5,
              flexWrap: { xs: 'wrap', sm: 'nowrap' },
              width: '100%'
            }}
          >
            <Avatar
              src={node.profilePicture}
              sx={{
                width: { xs: 28, sm: 32 },
                height: { xs: 28, sm: 32 },
                bgcolor: isRoot ? "primary.main" : "secondary.main",
                fontSize: { xs: "0.75rem", sm: "0.875rem" },
                flexShrink: 0
              }}
            >
              {node.name.charAt(0).toUpperCase()}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0, maxWidth: { xs: 'calc(100% - 120px)', sm: 'none' } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Typography 
                  variant="body2" 
                  fontWeight={isRoot ? "bold" : "medium"} 
                  noWrap
                  sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                >
                  {node.name}
                </Typography>
                {adsWatchedData[node.userId] && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {adsWatchedData[node.userId].status === 'Green' && adsWatchedData[node.userId].percentage >= 100 ? (
                      <ThumbUpIcon
                        sx={{
                          width: { xs: 14, sm: 16 },
                          height: { xs: 14, sm: 16 },
                          color: '#4caf50',
                          flexShrink: 0
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          width: { xs: 10, sm: 12 },
                          height: { xs: 10, sm: 12 },
                          borderRadius: '50%',
                          bgcolor: 
                            adsWatchedData[node.userId].status === 'Red' ? '#f44336' :
                            adsWatchedData[node.userId].status === 'Amber' ? '#ff9800' :
                            adsWatchedData[node.userId].status === 'Blue' ? '#2196f3' :
                            adsWatchedData[node.userId].status === 'Green' ? '#4caf50' : '#9e9e9e',
                          border: '1px solid rgba(255, 255, 255, 0.3)',
                          flexShrink: 0
                        }}
                      />
                    )}
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: { xs: '0.65rem', sm: '0.7rem' },
                        color: 'text.secondary',
                        fontWeight: 500
                      }}
                    >
                      {adsWatchedData[node.userId].adsWatched}/{adsWatchedData[node.userId].targetAds} {adsWatchedData[node.userId].percentage}%
                    </Typography>
                  </Box>
                )}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                <Typography 
                  variant="caption" 
                  color="text.secondary" 
                  noWrap
                  sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                >
                  @{node.userName}
                </Typography>
                {node.totalReferrals !== undefined && node.totalReferrals > 0 && (
                  <Chip
                    label={`${node.totalReferrals} ${node.totalReferrals === 1 ? 'referral' : 'referrals'}`}
                    size="small"
                    sx={{
                      height: { xs: 16, sm: 18 },
                      fontSize: { xs: '0.6rem', sm: '0.65rem' },
                      bgcolor: 'primary.main',
                      color: 'white',
                      fontWeight: 500,
                      '& .MuiChip-label': {
                        px: { xs: 0.5, sm: 0.75 }
                      }
                    }}
                  />
                )}
              </Box>
            </Box>
            <Stack 
              direction="row" 
              spacing={0.5}
              sx={{ 
                flexShrink: 0,
                flexWrap: { xs: 'wrap', sm: 'nowrap' },
                justifyContent: { xs: 'flex-start', sm: 'flex-end' },
                width: { xs: '100%', sm: 'auto' },
                mt: { xs: 0.5, sm: 0 }
              }}
            >
              {isRoot && (
                <Chip 
                  label="You" 
                  size="small" 
                  color="primary" 
                  sx={{ 
                    height: { xs: 18, sm: 20 }, 
                    fontSize: { xs: "0.6rem", sm: "0.65rem" } 
                  }} 
                />
              )}
              {!isRoot && (
                <Chip
                  label={`L${node.level}`}
                  size="small"
                  color={node.level <= 2 ? "success" : node.level <= 5 ? "info" : "default"}
                  sx={{ 
                    height: { xs: 18, sm: 20 }, 
                    fontSize: { xs: "0.6rem", sm: "0.65rem" } 
                  }}
                />
              )}
              {!node.isActive && (
                <Chip 
                  label="Inactive" 
                  size="small" 
                  color="error" 
                  sx={{ 
                    height: { xs: 18, sm: 20 }, 
                    fontSize: { xs: "0.6rem", sm: "0.65rem" } 
                  }} 
                />
              )}
              {isLoading && <CircularProgress size={14} sx={{ display: { xs: 'none', sm: 'block' } }} />}
            </Stack>
          </Box>
        }
      >
        {/* Recursively render all nested children - they're already loaded from API */}
        {children.length > 0 ? (
          children.map((child, index) => {
            // Child already has nested children in its structure, render recursively
            // renderTreeItem will handle nested children automatically
            console.log(`🔄 [renderTreeItem] About to render child ${index + 1}/${children.length}: ${child.userName} (Level ${child.level}, ID: ${child.id})`);
            return renderTreeItem(child);
          })
        ) : (
          hasChildren && !isLoading && (
            <TreeItem
              itemId={`no-children-${node.id}`}
              label={
                <Typography variant="caption" color="text.secondary" sx={{ pl: 3, fontStyle: "italic" }}>
                  No children found (but hasChildren=true)
                </Typography>
              }
            />
          )
        )}
        {isLoading && (
          <TreeItem
            itemId={`loading-${node.id}`}
            label={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.5, pl: 3 }}>
                <CircularProgress size={16} />
                <Typography variant="caption" color="text.secondary">
                  Loading referrals...
                </Typography>
              </Box>
            }
          />
        )}
        {!hasChildren && !isLoading && children.length === 0 && loadedChildren.has(node.referralCode) && (
          <TreeItem
            itemId={`empty-${node.id}`}
            label={
              <Typography variant="caption" color="text.secondary" sx={{ pl: 3, fontStyle: "italic" }}>
                No referrals yet
              </Typography>
            }
          />
        )}
      </TreeItem>
    );
  }, [loadingChildren, loadedChildren]);

  // Search functionality
  const handleSearch = useCallback(async () => {
    if (!searchTerm.trim() || !currentUserCode) {
      setSearchResults(null);
      return;
    }

    // For now, simple search - expand tree and search
    // TODO: Implement server-side search if needed
    setSearchResults(searchTerm.trim());
  }, [searchTerm, currentUserCode]);

  // Initialize on mount
  useEffect(() => {
    initializeRoot();
  }, [initializeRoot]);

  // Auto-load root's children when root is set
  useEffect(() => {
    if (rootNode && currentUserCode && !loadedChildren.has(currentUserCode)) {
      fetchDirectReferrals(currentUserCode);
    }
  }, [rootNode, currentUserCode, loadedChildren, fetchDirectReferrals]);

  // Build complete tree structure with all loaded children (MUST be before conditional returns)
  const treeData = useMemo(() => {
    return rootNode ? buildCompleteTree(rootNode) : null;
  }, [rootNode, buildCompleteTree]);

  // Calculate total loaded referrals (MUST be before conditional returns)
  const totalReferrals = useMemo(() => {
    if (!treeData) return 0;
    const countReferrals = (node) => {
      if (!node) return 0;
      const children = loadedChildren.get(node.referralCode) || [];
      return children.length + children.reduce((sum, child) => sum + countReferrals(child), 0);
    };
    return countReferrals(treeData);
  }, [treeData, loadedChildren]);

  // All hooks must be called before any conditional returns
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box mt={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!rootNode) {
    return (
      <Box mt={3}>
        <Alert severity="info">No community network found.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }}>
      <DashboardPageHeader
        icon={AccountTreeIcon}
        title="Community network"
        subtitle="Explore your referral tree, search members, and refresh the latest structure."
      >
        <Stack 
          direction={{ xs: 'column', sm: 'row' }} 
          spacing={1} 
          alignItems="stretch"
          sx={{ width: { xs: '100%', sm: 'auto' } }}
        >
          <TextField
            label="Search by name or username"
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            sx={{ 
              width: { xs: '100%', sm: 300 },
              minWidth: { xs: '100%', sm: 250 }
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={handleSearch} edge="end">
                    <SearchIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <IconButton 
            onClick={initializeRoot} 
            title="Refresh"
            sx={{ 
              alignSelf: { xs: 'flex-start', sm: 'center' },
              minWidth: { xs: '40px', sm: 'auto' }
            }}
          >
            <RefreshIcon />
          </IconButton>
        </Stack>
      </DashboardPageHeader>

      {/* Stats */}
      <Paper 
        sx={{ 
          p: { xs: 1.5, sm: 2 }, 
          mb: 2,
          borderRadius: { xs: 2, sm: 3 }
        }}
      >
        <Stack 
          direction={{ xs: 'column', sm: 'row' }}
          spacing={{ xs: 2, sm: 3 }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography 
              variant="caption" 
              color="text.secondary"
              sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
            >
              Total Community Connections
            </Typography>
            <Typography 
              variant="h6" 
              color="primary"
              sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}
            >
              {totalCommunityConnections.toLocaleString()}
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography 
              variant="caption" 
              color="text.secondary"
              sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
            >
              {`Loaded Connections (up to Level ${COMMUNITY_NETWORK_MAX_DEPTH})`}
            </Typography>
            <Typography 
              variant="h6" 
              color="secondary"
              sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}
            >
              {totalReferrals.toLocaleString()}
            </Typography>
            <Chip
              label={`Showing up to Level ${COMMUNITY_NETWORK_MAX_DEPTH}`}
              size="small"
              color="info"
              variant="outlined"
              sx={{ 
                mt: 0.5,
                fontSize: { xs: '0.65rem', sm: '0.7rem' },
                height: { xs: '20px', sm: '24px' }
              }}
            />
          </Box>
        </Stack>
      </Paper>

      {/* Tree View */}
      <Paper 
        sx={{ 
          p: { xs: 1, sm: 2 }, 
          minHeight: { xs: 400, sm: 500 }, 
          maxHeight: { xs: "60vh", sm: "70vh" }, 
          overflow: "auto",
          borderRadius: { xs: 2, sm: 3 }
        }}
      >
        <SimpleTreeView
          expandedItems={Array.from(expandedNodes)}
          onExpandedItemsChange={handleNodeToggle}
          defaultCollapseIcon={<ExpandMoreIcon />}
          defaultExpandIcon={<ChevronRightIcon />}
        >
          {renderTreeItem(treeData)}
        </SimpleTreeView>
      </Paper>
    </Box>
  );
};

export default ReferralTreeLazy;

